import { useSyncExternalStore } from "react";

/**
 * La photo de profil, tenue hors de React comme le profil et la voix.
 *
 * Elle vit sur le téléphone (localStorage) ; avec un compte, le serveur la
 * garde aussi et fait foi (une photo retirée par la régie disparaît de tous
 * les téléphones). La tribu la voit : le téléphone l'envoie en entrant.
 *
 * L'image est réduite ici, avant tout envoi : un carré de 320 px en JPEG,
 * quelques kilo-octets. Le serveur refuse le reste.
 */

const CLE = "hoa.photo";
// 320 px : la photo s'ouvre en grand depuis « Mon compte ». La qualité baisse
// d'elle-même pour tenir sous la limite du serveur.
const TAILLE = 320;
const MAX_CARACTERES = 58_000;

let courante: string | null = (() => {
  try {
    return localStorage.getItem(CLE);
  } catch {
    return null;
  }
})();
const abonnes = new Set<() => void>();

export function photoCourante(): string | null {
  return courante;
}

export function poserPhoto(photo: string | null): void {
  if (photo === courante) return;
  courante = photo;
  try {
    if (photo) localStorage.setItem(CLE, photo);
    else localStorage.removeItem(CLE);
  } catch {
    /* navigation privée : la photo vaut pour la visite */
  }
  for (const f of abonnes) f();
}

export function usePhoto(): string | null {
  return useSyncExternalStore(
    (f) => {
      abonnes.add(f);
      return () => abonnes.delete(f);
    },
    () => courante,
  );
}

/** Un fichier choisi → un carré de 320 px en JPEG, recadré au centre. */
export async function reduireImage(fichier: Blob): Promise<string> {
  const url = URL.createObjectURL(fichier);
  try {
    const img = await new Promise<HTMLImageElement>((ok, ko) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => ko(new Error("Cette image ne s'ouvre pas."));
      i.src = url;
    });
    const cote = Math.min(img.naturalWidth, img.naturalHeight);
    if (!cote) throw new Error("Cette image ne s'ouvre pas.");
    const toile = document.createElement("canvas");
    toile.width = TAILLE;
    toile.height = TAILLE;
    const ctx = toile.getContext("2d");
    if (!ctx) throw new Error("Ce navigateur ne sait pas réduire l'image.");
    ctx.drawImage(img, (img.naturalWidth - cote) / 2, (img.naturalHeight - cote) / 2, cote, cote, 0, 0, TAILLE, TAILLE);
    for (const q of [0.85, 0.7, 0.55, 0.4]) {
      const d = toile.toDataURL("image/jpeg", q);
      if (d.length <= MAX_CARACTERES) return d;
    }
    throw new Error("Cette image est trop lourde, même réduite.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** L'empreinte que le serveur recalcule (photos.ts) : SHA-256, 32 premiers caractères hexadécimaux. */
export async function empreintePhoto(photo: string): Promise<string> {
  const h = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(photo));
  return [...new Uint8Array(h)]
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}
